<script setup lang="ts">
  import { computed } from 'vue';
  import { useCustomToast } from '@/composables/useCustomToast';
  import CustomToast from '@/components/elements/CustomToast/CustomToast.vue';

  const props = defineProps<{ id: string; campaignName: string; channel: string; status: string; startDate: string; endDate: string }>();

  const emits = defineEmits(['close-modal']);
  const { showToast } = useCustomToast();

  const closeModal = () => {
    emits('close-modal');
  };

  const shareableLink = computed(() => {
    const baseUrl = window.location.origin; // Gets "http://localhost:5173" or production URL
    const campaignId = props.id;
    const queryParams = new URLSearchParams({
      campaign_name: props.campaignName,
      channel: props.channel,
      status: props.status,
      start_date: props.startDate,
      end_date: props.endDate,
    }).toString();

    const publicLink = `${baseUrl}/sme/public/reports/${campaignId}?${queryParams}`;

    return publicLink;
  });

  const copyLink = () => {
    navigator.clipboard.writeText(shareableLink.value);
    showToast('Link copied to clipboard!');
  };
</script>

<template>
  <CustomToast />
  <Teleport to="body">
    <div class="modal-overlay" @click.self="emits('close-modal')">
      <div class="share-report-modal flex flex-col gap-12">
        <div class="modal-header">
          <div class="flex items-center gap-8">
            <div class="icon-container">
              <iconify-icon icon="ic:baseline-insights" />
            </div>
            <h2 class="share-modal-heading">Share Report</h2>
          </div>
          <button class="close-btn" @click="closeModal">
            <iconify-icon icon="lucide:x" />
          </button>
        </div>
        <div class="modal-body">
          <p class="share-info">Share this report with stakeholders using the link below. Anyone with this link can view the report.</p>
          <div class="share-link-wrapper flex flex-col gap-8">
            <p>Shareable link</p>
            <div class="share-link-container flex items-center gap-8">
              <input type="text" v-model="shareableLink" readonly />
              <button class="copy-btn flex items-center justify-center" @click="copyLink">
                <iconify-icon icon="lucide:copy" />
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer flex items-center gap-8">
          <button class="copy-btn" @click="copyLink">Copy link</button>
          <button class="close-btn" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
  @import '@/assets/styles/variables.scss';

  .modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;

    @media screen and (min-width: 768px) {
      padding: 20px;
    }
  }

  .share-report-modal {
    background: #f8fafc;
    border-radius: 16px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    padding: 16px;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);

    @media screen and (min-width: 768px) {
      max-width: 600px;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50%;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: $primary-main;
      color: white;
    }

    .share-modal-heading {
      font-size: 20px;
      color: $neutral-black;
      font-weight: 600;
      line-height: unset;
    }
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .share-info {
      font-size: 14px;
      color: $neutral-gray;
    }

    .share-link-wrapper {
      p {
        color: $neutral-black;
        font-size: 14px;
        font-weight: 500;
      }

      .share-link-container {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid $input-border;
        border-radius: 8px;
        padding: 8px;
        background-color: white;
      }

      input {
        font-size: 14px;
        width: 100%;
        border: none;
        outline: none;
        background-color: transparent;
      }

      .copy-btn {
        border: none;
        background: $primary-main;
        color: white;
        cursor: pointer;
        border-radius: 8px;
        width: 30px;
        height: 30px;
      }
    }
  }

  .modal-footer {
    button {
      width: 100%;
      border-radius: 8px;
      padding-block: 4px;
      min-height: 46px;

      &.copy-btn {
        background: linear-gradient(to right, #3b82f6, #14b8a6);
        color: white;
        cursor: pointer;
      }

      &.close-btn {
        background: white;
        border: 1px solid $input-border;
        color: $neutral-black;
        cursor: pointer;
      }
    }
  }
</style>
